import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const AttentionDocumentTimesSeriesChart = ({ data, isLoading }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      const validData = data.filter((item) => {
        const datesData = item?.header?.detail?.dates_data;
        const dateInit = new Date(datesData?.date_init);
        return !isNaN(dateInit.getTime()); // Ensure date_init is valid
      });

      const groupedData = validData.reduce((acc, item) => {
        const datesData = item?.header?.detail?.dates_data;
        const dateInit = new Date(datesData?.date_init)
          .toISOString()
          .split("T")[0];
        const attentionTime = calculateAttentionTimeValue(datesData);
        const docTime = calculateDocTimeValue(datesData);

        if (!acc[dateInit]) {
          acc[dateInit] = { attentionTimes: [], docTimes: [] };
        }
        if (attentionTime !== null) {
          acc[dateInit].attentionTimes.push(attentionTime);
        }
        if (docTime !== null) {
          acc[dateInit].docTimes.push(docTime);
        }

        return acc;
      }, {});

      const labels = Object.keys(groupedData).sort();

      let cumulativeAttentionSum = 0;
      let cumulativeAttentionCount = 0;
      const avgAttentionTimes = labels.map((label) => {
        const attentionTimes = groupedData[label].attentionTimes || [];
        cumulativeAttentionSum += attentionTimes.reduce((a, b) => a + b, 0);
        cumulativeAttentionCount += attentionTimes.length;
        return cumulativeAttentionCount === 0
          ? 0
          : cumulativeAttentionSum / cumulativeAttentionCount;
      });

      let cumulativeDocSum = 0;
      let cumulativeDocCount = 0;
      const avgDocTimes = labels.map((label) => {
        const docTimes = groupedData[label].docTimes || [];
        cumulativeDocSum += docTimes.reduce((a, b) => a + b, 0);
        cumulativeDocCount += docTimes.length;
        return cumulativeDocCount === 0
          ? 0
          : cumulativeDocSum / cumulativeDocCount;
      });

      // Only set chartData if data is available
      if (labels.length > 0) {
        setChartData({
          labels,
          datasets: [
            {
              label: "Cumulative Average Attention Time (hours)",
              data: avgAttentionTimes,
              borderColor: "rgba(0, 128, 0, 0.6)",
              fill: false,
            },
            {
              label: "Cumulative Average Document Time (hours)",
              data: avgDocTimes,
              borderColor: "rgba(255, 99, 132, 0.6)",
              fill: false,
            },
          ],
        });
      }
    }
  }, [data, isLoading]);

  const options = {
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
        },
      },
      y: {
        beginAtZero: true,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
      datalabels: {
        display: false, // Disable data labels
      },
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800">
          Cumulative Attention & Document Times Over Time
        </h2>
      </div>
      <div className="flex-grow relative">
        {isLoading || !chartData ? (
          <div className="flex justify-center items-center h-full">
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

const calculateAttentionTimeValue = (datesData) => {
  const dateInit = new Date(datesData?.date_init);
  const dateCrossing = new Date(datesData?.date_crossing);

  if (isNaN(dateInit.getTime()) || isNaN(dateCrossing.getTime())) {
    return null; // Return null for invalid dates
  }
  return (dateCrossing - dateInit) / (1000 * 60 * 60);
};

const calculateDocTimeValue = (datesData) => {
  const dateInit = new Date(datesData?.date_init);
  const dateDoda = new Date(datesData?.date_doda);

  if (
    isNaN(dateInit.getTime()) ||
    isNaN(dateDoda.getTime()) ||
    dateDoda <= dateInit
  ) {
    return null; // Return null for invalid dates
  }
  return (dateDoda - dateInit) / (1000 * 60 * 60);
};

export default AttentionDocumentTimesSeriesChart;
